import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { ORDER_STATUS_LABELS } from '@flashcard/shared';
import type { Order } from '@flashcard/shared';

export function OrderStatusPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const data = await api.getOrder(id!);
        setOrder(data);
        if (['draft', 'payment_pending', 'pending', 'quoted', 'confirmed', 'production', 'shipped'].includes(data.status)) {
          setPolling(true);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();

    const interval = setInterval(() => {
      if (polling) fetchOrder();
    }, 10000);
    return () => clearInterval(interval);
  }, [id, polling]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="loading" style={{ width: '2rem', height: '2rem' }} />
        <p className="text-muted">Lädt Bestellung…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <p className="error">Fehler: {error}</p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Zurück
        </button>
      </div>
    );
  }
  if (!order) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <p className="text-muted">Bestellung nicht gefunden</p>
      </div>
    );
  }

  const statusLabel = ORDER_STATUS_LABELS[order.status] || order.status;
  const isFinal = ['delivered', 'cancelled', 'failed'].includes(order.status);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Bestellstatus</h2>
        {!isFinal && (
          <button className="btn btn-secondary" onClick={() => setPolling(true)}>
            Aktualisieren
          </button>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.25rem' }}>
            Referenz: <strong>{order.orderReferenceId}</strong>
          </span>
          <span className={`pill ${isFinal ? 'pill-done' : 'pill-progress'}`}>{statusLabel}</span>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <strong>Lieferadresse:</strong>
          <br />
          <pre
            style={{
              background: 'var(--bg-elevated)',
              padding: '1rem',
              borderRadius: '8px',
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              color: 'var(--text)',
            }}
          >
            {typeof order.shippingAddress === 'string'
              ? order.shippingAddress
              : JSON.stringify(order.shippingAddress, null, 2)}
          </pre>
        </div>

        <h3>Positionen</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {(order as any).items?.map((item: any, i: number) => (
            <li
              key={i}
              style={{
                padding: '0.5rem 0',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span>
                {item.productUid} × {item.quantity}
              </span>
              <span className={`pill ${item.status === 'completed' ? 'pill-done' : 'pill-progress'}`}>
                {item.status}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <button className="btn btn-secondary" onClick={() => navigate('/')} style={{ marginTop: '1rem' }}>
        Neue Bestellung
      </button>
    </div>
  );
}
